import { CustomerService } from "../../Services/customerService";
import dashlyLogoAsset from "../../Images/logonew.png";

export const CUSTOMER_LOGOS_STORAGE_KEY = "customerLogos";
export const CUSTOMER_LOGOS_WIDE_STORAGE_KEY = "customerLogosWide";

/** Matches sidebar `.dashly_icon` (55px height, ~80px max width at 96dpi). */
export const PDF_LOGO_HEIGHT_MM = 14.5;
export const PDF_LOGO_MAX_WIDTH_MM = 21;

/** ~3× CSS display size (~80px) for sharp PDF embed without huge bitmaps. */
const PDF_LOGO_EMBED_MAX_WIDTH_PX = 240;
const PDF_LOGO_JPEG_QUALITY = 0.82;

export interface IProjectPdfLogo {
    dataUrl: string;
    format: "PNG" | "JPEG";
    widthMm: number;
    heightMm: number;
}

const readCachedLogo = (storageKey: string, customerUuid: string): string | null => {
    try {
        const stored = localStorage.getItem(storageKey);
        if (!stored) {
            return null;
        }
        const logos = JSON.parse(stored) as Record<string, string>;
        const logo = logos[customerUuid];
        return logo?.trim() ? logo : null;
    } catch {
        return null;
    }
};

const writeCachedLogo = (storageKey: string, customerUuid: string, logo: string) => {
    try {
        const stored = localStorage.getItem(storageKey);
        const logos = stored ? (JSON.parse(stored) as Record<string, string>) : {};
        logos[customerUuid] = logo;
        localStorage.setItem(storageKey, JSON.stringify(logos));
    } catch {
        // Cache is best-effort for PDF export.
    }
};

const isEmbeddableImageDataUrl = (value: string): boolean => value.trim().startsWith("data:");

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("Failed to load image"));
        image.src = dataUrl;
    });

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error("Failed to read image blob"));
        reader.readAsDataURL(blob);
    });

const fetchImageAsDataUrl = async (url: string): Promise<string> => {
    const response = await fetch(url, { credentials: "same-origin" });
    if (!response.ok) {
        throw new Error(`Failed to fetch image (${response.status})`);
    }
    return blobToDataUrl(await response.blob());
};

const hasMeaningfulAlpha = (context: CanvasRenderingContext2D, width: number, height: number): boolean => {
    const { data } = context.getImageData(0, 0, width, height);
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 250) {
            return true;
        }
    }
    return false;
};

/**
 * Downscale to ~3× display size and prefer JPEG (q≈0.82) for opaque logos so PDF
 * embeddings stay small. Keep PNG only when the source has meaningful alpha.
 */
const normalizeLogoForPdf = async (dataUrl: string): Promise<{ dataUrl: string; format: "PNG" | "JPEG" }> => {
    const image = await loadImage(dataUrl);
    const naturalWidth = Math.max(1, image.naturalWidth || 1);
    const naturalHeight = Math.max(1, image.naturalHeight || 1);
    const scale = Math.min(1, PDF_LOGO_EMBED_MAX_WIDTH_PX / naturalWidth);
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) {
        throw new Error("Failed to prepare logo image");
    }

    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    if (hasMeaningfulAlpha(context, width, height)) {
        return { dataUrl: canvas.toDataURL("image/png"), format: "PNG" };
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    return {
        dataUrl: canvas.toDataURL("image/jpeg", PDF_LOGO_JPEG_QUALITY),
        format: "JPEG",
    };
};

const resolveImageSource = async (imageUrl: string): Promise<{ dataUrl: string; format: "PNG" | "JPEG" }> => {
    const trimmed = imageUrl.trim();
    if (!trimmed) {
        throw new Error("Empty image source");
    }
    if (isEmbeddableImageDataUrl(trimmed)) {
        return normalizeLogoForPdf(trimmed);
    }
    const fetchedDataUrl = await fetchImageAsDataUrl(trimmed);
    return normalizeLogoForPdf(fetchedDataUrl);
};

const fitLogoDimensions = (naturalWidth: number, naturalHeight: number): { widthMm: number; heightMm: number } => {
    if (naturalWidth <= 0 || naturalHeight <= 0) {
        return { widthMm: PDF_LOGO_MAX_WIDTH_MM * 0.7, heightMm: PDF_LOGO_HEIGHT_MM };
    }

    const aspectRatio = naturalWidth / naturalHeight;
    let heightMm = PDF_LOGO_HEIGHT_MM;
    let widthMm = heightMm * aspectRatio;
    if (widthMm > PDF_LOGO_MAX_WIDTH_MM) {
        widthMm = PDF_LOGO_MAX_WIDTH_MM;
        heightMm = widthMm / aspectRatio;
    }
    return { widthMm, heightMm };
};

const fetchCustomerLogoDataUrl = async (customerUuid: string): Promise<string | null> => {
    const customerService = new CustomerService();

    try {
        const wideResponse = await customerService.getCustomerLogoWide(customerUuid);
        const wideLogo = wideResponse.data?.data?.logo?.trim();
        if (wideLogo && isEmbeddableImageDataUrl(wideLogo)) {
            writeCachedLogo(CUSTOMER_LOGOS_WIDE_STORAGE_KEY, customerUuid, wideLogo);
            return wideLogo;
        }
    } catch {
        // Fall back to square logo.
    }

    try {
        const logoResponse = await customerService.getCustomerLogo(customerUuid);
        const logo = logoResponse.data?.data?.logo?.trim();
        if (logo && isEmbeddableImageDataUrl(logo)) {
            writeCachedLogo(CUSTOMER_LOGOS_STORAGE_KEY, customerUuid, logo);
            return logo;
        }
    } catch {
        return null;
    }

    return null;
};

const preparePdfLogo = async (imageUrl: string): Promise<IProjectPdfLogo> => {
    const compressed = await resolveImageSource(imageUrl);
    const image = await loadImage(compressed.dataUrl);
    const { widthMm, heightMm } = fitLogoDimensions(image.naturalWidth, image.naturalHeight);
    return {
        dataUrl: compressed.dataUrl,
        format: compressed.format,
        widthMm,
        heightMm,
    };
};

let dashlyPdfLogoCache: IProjectPdfLogo | null = null;

export const resolveDashlyPdfLogo = async (): Promise<IProjectPdfLogo | null> => {
    if (dashlyPdfLogoCache) {
        return dashlyPdfLogoCache;
    }
    try {
        dashlyPdfLogoCache = await preparePdfLogo(dashlyLogoAsset);
        return dashlyPdfLogoCache;
    } catch {
        return null;
    }
};

const readEmbeddableCachedLogo = (storageKey: string, customerUuid: string): string | null => {
    const cachedLogo = readCachedLogo(storageKey, customerUuid);
    if (!cachedLogo || !isEmbeddableImageDataUrl(cachedLogo)) {
        return null;
    }
    return cachedLogo;
};

export const resolveCustomerPdfLogo = async (customerUuid?: string): Promise<IProjectPdfLogo | null> => {
    if (!customerUuid?.trim()) {
        return null;
    }

    const cachedLogo =
        readEmbeddableCachedLogo(CUSTOMER_LOGOS_WIDE_STORAGE_KEY, customerUuid) ??
        readEmbeddableCachedLogo(CUSTOMER_LOGOS_STORAGE_KEY, customerUuid);
    const logoDataUrl = cachedLogo ?? (await fetchCustomerLogoDataUrl(customerUuid));
    if (!logoDataUrl?.trim()) {
        return null;
    }

    try {
        return await preparePdfLogo(logoDataUrl);
    } catch {
        return null;
    }
};
